/* WeQuote Dev Board — Roadmap (renderRoadmap + roadPanelHtml, faithful port).
 * Per-developer lanes in build order under per-lane month colour-bands (lanes
 * do NOT share one axis — the month colours are the shared time reference);
 * dated cards placed by the schedule, a grey coming-soon zone for cards
 * missing a deadline/estimate, shaded block-out tiles, pins with conflicts,
 * collapse cards / collapse lanes / re-tidy, drag to reorder or assign, and
 * the right-hand Issues & actions panel (At risk / Blockers / Conflicts /
 * Accepted risks + the Unscheduled drag groups). Cards open the shared pop-up.
 * The customer-roadmap export button arrives with the Completed phase. */
import React, { useRef, useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as SC from '../core/schedule'
import * as SG from '../core/suggest'
import type { Feature, Blockout } from '../core/types'
import { DEVCOLS, DL, RM_CW, RM_CWC, RM_GAP, RM_MON, RM_MON_COLORS, timeLabel, SLGet } from '../core/constants'
import { fmtDate } from '../core/dates'
import { cardStatus, checklistProgress, isLate, hasEstimate } from '../core/logic'
import * as M from '../core/mutations'
import { IcFlag, IcPin, IcClock, IcShare, IcPrinter, IcDownload, IcCalendar } from '../icons'
import * as RX from '../core/customerroadmap'
import { custDisplayName, custDescOf } from '../core/logic'

type DragKind = 'lane' | 'unsched'

export const RoadmapView: React.FC = () => {
  const { cards, blockouts, sched, mutate, openCardModal, addBlockoutAction, removeBlockoutAction, showToast, openSubModal } = useDevBoard()
  const [collapsedLanes, setCollapsedLanes] = useState<Record<string, boolean>>({})
  const [cardsCollapsed, setCardsCollapsed] = useState(false)
  const [panelOpen, setPanelOpen] = useState<Record<string, boolean>>({ atrisk: true })
  const [blockForm, setBlockForm] = useState(false)
  const [bo, setBo] = useState({ name: '', start: '', end: '', scope: 'all' })
  const [confirmBO, setConfirmBO] = useState<Blockout | null>(null)
  const [rxOpen, setRxOpen] = useState(false)
  const [rxRange, setRxRange] = useState<RX.RxRange>('all')
  const [rxReleased, setRxReleased] = useState(true)
  const drag = useRef<{ id: number; kind: DragKind } | null>(null)

  const cw = cardsCollapsed ? RM_CWC : RM_CW
  const byPo = (a: Feature, b: Feature) => a.po - b.po

  const roadDrop = (targetId: number) => {
    const d = drag.current
    drag.current = null
    if (!d) return
    if (d.kind === 'unsched') {
      const t = cards.find((x) => x.id === targetId)
      if (t && t.dev) mutate((fs) => SC.assignToLane(fs, d.id, t.dev, blockouts))
    } else if (d.id !== targetId) {
      mutate((fs) => SG.reorder(fs, d.id, targetId))
    }
  }
  const roadDropLane = (dev: string) => {
    const d = drag.current
    drag.current = null
    if (d && d.kind === 'unsched') mutate((fs) => SC.assignToLane(fs, d.id, dev, blockouts))
  }

  /* ── v2 §2: one roadmap card per scheduled stage; done stages dim ── */
  const StageCard: React.FC<{ u: SC.StageUnit }> = ({ u }) => (
    <div className={'rm-card rm-stagecard' + (u.s.done ? ' done' : '')} style={{ width: cw }} onClick={() => openCardModal(u.f.id)} title={SC.stageLabel(u.f, u.i)}>
      <div className="rm-c-name">{SC.stageLabel(u.f, u.i)}</div>
      <div className="rm-c-stage">{u.s.done ? 'Stage complete' : (u.s.dev && DL[u.s.dev] ? DL[u.s.dev] : DL[u.f.dev] || 'Unassigned')}</div>
      <div className="rm-c-dates">{fmtDate(u.s.schedStart)} → {fmtDate(u.s.estCompletion)}{u.s.deadline ? ' · due ' + fmtDate(u.s.deadline) : ''}</div>
      {u.s.start && <span className="rm-pin on" title="Stage start pinned"><IcPin size={12} /></span>}
    </div>
  )

  /* ── road cards ── */
  const RoadCard: React.FC<{ f: Feature }> = ({ f }) => {
    const st = cardStatus(f)
    const risk = st.cls === 'atrisk' || st.cls === 'overdue'
    const late = isLate(f)
    const p = checklistProgress(f)
    const blk = f.blocker ? <span className="rm-blk" title="Blocker"><IcFlag size={11} /> </span> : null
    const dragProps = {
      draggable: true,
      onDragStart: () => { drag.current = { id: f.id, kind: 'lane' } },
      onDragOver: (e: React.DragEvent) => e.preventDefault(),
      onDrop: (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); roadDrop(f.id) },
    }
    if (SC.isPreDev(f)) {
      const showD = f.status === 'design' || f.status === 'designcomplete'
      const miss = !hasEstimate(f) ? 'needs an estimate' : !f.deadline && !f.start ? 'needs a start date or deadline' : ''
      return (
        <div className={'rm-card pre' + (risk ? ' late' : '')} style={{ width: cw }} {...dragProps} onClick={() => openCardModal(f.id)} title={f.title}>
          <div className="rm-c-name">{blk}{f.title}</div>
          <div className="rm-c-stage">{SLGet(f.status)}</div>
          {showD && <><div className="pbar mini"><div className="pbar-fill" style={{ width: (f.designProgress || 0) + '%' }} /></div><div className="rm-c-pct">{f.designProgress || 0}% designed</div></>}
          {risk && <div className="rm-c-status"><span className={'st-chip st-' + st.cls}>{st.label}</span></div>}
          {miss && <div className="rm-c-miss">{miss}</div>}
        </div>
      )
    }
    if (cardsCollapsed) {
      return (
        <div className={'rm-card thin' + (risk ? ' late' : '')} style={{ width: cw }} {...dragProps} onClick={() => openCardModal(f.id)} title={f.title}>
          <div className="rm-c-name">{blk}{f.title}</div>
          <div className="pbar mini"><div className="pbar-fill" style={{ width: (f.progress || 0) + '%' }} /></div>
          {risk && <div className="rm-c-late">{late ? SC.daysLate(f) + 'd late' : st.label}</div>}
        </div>
      )
    }
    return (
      <div className={'rm-card' + (risk ? ' late' : '')} style={{ width: cw }} {...dragProps}>
        <div className="rm-c-top">
          <span className="rm-c-name" onClick={() => openCardModal(f.id)}>{blk}{f.title}</span>
          <span className={'rm-pin' + (f.pinnedStart ? ' on' : '')} title={f.pinnedStart ? 'Pinned start (click to unpin)' : 'Pin start'} onClick={() => mutate((fs) => SC.togglePin(fs, f.id, sched.start))}><IcPin size={12} /></span>
        </div>
        <div className="pbar"><div className="pbar-fill" style={{ width: (f.progress || 0) + '%' }} /></div>
        <div className="rm-c-pct">{f.progress || 0}% complete</div>
        <div className="rm-c-dates">Est <b>{f.estCompletion ? fmtDate(f.estCompletion) : '—'}</b> · Due <b>{f.deadline ? fmtDate(f.deadline) : '—'}</b></div>
        <div className="rm-c-status"><span className={'st-chip st-' + st.cls}>{st.label}</span>{late && <> <span className="rm-red">{SC.daysLate(f)}d late</span></>}</div>
        <div className="rm-c-foot">{p.total ? <span className={'rm-c-chk' + (p.done === p.total ? ' done' : '')}>✓ {p.done}/{p.total}</span> : <span />}<span className="rm-c-est">{f.time ? timeLabel(f.time) : 'no est'}</span></div>
      </div>
    )
  }

  const BlockTile: React.FC<{ b: Blockout }> = ({ b }) => (
    <div className="rm-block" style={{ width: SC.blockW(b) }} title={b.name + ' (' + fmtDate(b.start) + '–' + fmtDate(b.end) + ')'}>
      <div className="rm-block-n"><IcClock size={11} /> {b.name}</div>
      <div className="rm-block-d">{fmtDate(b.start)}–{fmtDate(b.end)}</div>
    </div>
  )

  /* ── lanes ── */
  const lanes = DEVCOLS.map((dev) => {
    const dated = cards.filter((f) => f.dev === dev && SC.datedCard(f) && !f.multiStage).sort(SC.laneSort)
    const soon = cards.filter((f) => f.dev === dev && SC.comingSoon(f) && !f.multiStage).sort(SC.laneSort)
    const stageU = SC.stageUnits(cards, dev) // v2 §2: one card per scheduled stage
    if (!dated.length && !soon.length && !stageU.length) return null
    const collapsed = !!collapsedLanes[dev]
    const devBO = blockouts.filter((b) => b.scope === 'all' || b.scope === dev)
    interface Item { t: 'card' | 'block' | 'stage'; f?: Feature; b?: Blockout; u?: SC.StageUnit; d: string }
    const items: Item[] = []
    dated.forEach((f) => items.push({ t: 'card', f, d: sched.start[f.id] || f.deadline || '9999-12' }))
    stageU.forEach((u) => items.push({ t: 'stage', f: u.f, u, d: u.s.schedStart || '9999-12' } as any))
    devBO.forEach((b) => items.push({ t: 'block', b, d: b.start }))
    items.sort((a, b) => (a.d < b.d ? -1 : a.d > b.d ? 1 : 0))
    const groups: { m: string; w: number }[] = []
    items.forEach((it) => {
      const m = it.d && it.d !== '9999-12' ? it.d.slice(0, 7) : '?'
      const w = (it.t === 'block' ? SC.blockW(it.b!) : cw) + RM_GAP // stage items are card-width — only true block-outs use blockW (crash fix, Lee 3 Sep)
      const g = groups[groups.length - 1]
      if (g && g.m === m) g.w += w
      else groups.push({ m, w })
    })
    return (
      <div key={dev} className="rm-lane">
        <div className="rm-lane-hd">
          <span className="rm-caret" onClick={() => setCollapsedLanes((c) => ({ ...c, [dev]: !c[dev] }))}>{collapsed ? '▸' : '▾'}</span>
          <span className="rm-lane-name" onClick={() => setCollapsedLanes((c) => ({ ...c, [dev]: !c[dev] }))}>{DL[dev]}</span>
          <span className="rm-lane-n">{dated.length + soon.length}</span>
          <button className="rm-tidy" title="Clear pins & repack this lane" onClick={() => mutate((fs) => SC.retidyLane(fs, dev))}>Re-tidy</button>
        </div>
        {!collapsed && (
          <div className="rm-lane-body" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); roadDropLane(dev) }}>
            <div className="rm-track">
              <div className="rm-months">
                {groups.map((g, i) => {
                  const mi = g.m === '?' ? -1 : parseInt(g.m.slice(5, 7), 10) - 1
                  return <div key={i} className="rm-mon" style={{ width: g.w, background: mi < 0 ? '#f3f4f6' : RM_MON_COLORS[mi] }}>{mi < 0 ? ' ' : RM_MON[mi] + ' ’' + g.m.slice(2, 4)}</div>
                })}
                {soon.length > 0 && <div className="rm-mon soon" style={{ width: soon.length * (cw + RM_GAP) }}>Coming soon — needs an estimate + a start date or deadline</div>}
              </div>
              <div className="rm-cards">
                {items.map((it) => (it.t === 'card' ? <RoadCard key={'c' + it.f!.id} f={it.f!} />
                  : it.t === 'stage' ? <StageCard key={'g' + it.f!.id + ':' + (it as any).u.i} u={(it as any).u} />
                  : <BlockTile key={'b' + it.b!.id} b={it.b!} />))}
                {soon.map((f) => <RoadCard key={'s' + f.id} f={f} />)}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }).filter(Boolean)

  /* ── right panel ── */
  const sev = (f: Feature) => (cardStatus(f).cls === 'overdue' ? 2 : 1)
  const atrisk = cards.filter((f) => SC.rmAtRisk(f) && !f.riskAcknowledged).sort((a, b) => sev(b) - sev(a) || SC.daysLate(b) - SC.daysLate(a))
  const conflicts = cards.filter((f) => sched.conflict[f.id])
  const accepted = cards.filter((f) => SC.rmAtRisk(f) && f.riskAcknowledged)
  const blockers = cards.filter((f) => SC.onRoadmap(f) && f.blocker).sort(byPo)
  const unB = cards.filter((f) => f.status === 'now' && SC.unscheduled(f)).sort(byPo) // v2 §5: Now band only
  const unD = cards.filter((f) => f.status === 'design' && SC.unscheduled(f)).sort(byPo)
  const unC = cards.filter((f) => f.status === 'designcomplete' && SC.unscheduled(f)).sort(byPo)

  const Cat: React.FC<{ k: string; label: string; list: Feature[]; row: (f: Feature) => React.ReactNode }> = ({ k, label, list, row }) => {
    const open = !!panelOpen[k]
    return (
      <div className="rm-cat">
        <div className="rm-cat-hd" onClick={() => setPanelOpen((p) => ({ ...p, [k]: !p[k] }))}>
          <span className="rm-cat-car">{open ? '▾' : '▸'}</span><span className="rm-cat-l">{label}</span><span className={'rm-cat-n' + (list.length ? ' hot' : '')}>{list.length}</span>
        </div>
        {open && <div className="rm-cat-body">{list.length ? list.map((f) => <React.Fragment key={f.id}>{row(f)}</React.Fragment>) : <div className="rm-cat-empty">None</div>}</div>}
      </div>
    )
  }

  return (
    <div id="roadmapView" style={{ padding: '16px 24px' }}>
      <div className="rm-wrap">
        <div className="rm-main">
          <div className="rm-toolbar">
            <button className="rm-tbtn" onClick={() => setCardsCollapsed((v) => !v)}>{cardsCollapsed ? 'Expand cards' : 'Collapse cards'}</button>
            <button className="rm-tbtn" onClick={() => setBlockForm((v) => !v)}><IcClock size={12} /> Block out time</button>
            <button className="rm-tbtn" onClick={() => setRxOpen(true)}><IcShare size={12} /> Export customer roadmap</button>
            <div className="rm-chips">
              {blockouts.map((b) => (
                <span key={b.id} className="rm-chip"><b>{b.name}</b> · {b.scope === 'all' ? 'All' : DL[b.scope] || b.scope} · {fmtDate(b.start)}–{fmtDate(b.end)} <button className="rm-chip-x" onClick={() => setConfirmBO(b)}>×</button></span>
              ))}
            </div>
          </div>
          {blockForm && (
            <div className="rm-boform">
              <input className="rm-in" placeholder="Name (e.g. Christmas shutdown)" value={bo.name} onChange={(e) => setBo((s) => ({ ...s, name: e.target.value }))} />
              <label>From <input type="date" className="rm-in" value={bo.start} onChange={(e) => setBo((s) => ({ ...s, start: e.target.value }))} /></label>
              <label>To <input type="date" className="rm-in" value={bo.end} onChange={(e) => setBo((s) => ({ ...s, end: e.target.value }))} /></label>
              <select className="rm-in" value={bo.scope} onChange={(e) => setBo((s) => ({ ...s, scope: e.target.value }))}>
                <option value="all">Everyone</option>
                {DEVCOLS.map((d) => <option key={d} value={d}>{DL[d]}</option>)}
              </select>
              <button className="rm-tbtn primary" onClick={() => { void addBlockoutAction(bo).then(() => { setBlockForm(false); setBo({ name: '', start: '', end: '', scope: 'all' }) }) }}>Add</button>
              <button className="rm-tbtn" onClick={() => setBlockForm(false)}>Cancel</button>
            </div>
          )}
          {lanes.length ? lanes : <div className="rm-empty">No cards on the roadmap yet. Assign a developer to a card — or drag one from the right — to place it on a lane.</div>}
        </div>
        <div className="rm-side">
          <div className="rm-side-hd">Issues &amp; actions</div>
          <Cat k="atrisk" label="At risk" list={atrisk} row={(f) => (
            <div className="rm-row"><div className="rm-row-t" onClick={() => openCardModal(f.id)}>{f.title}</div><div className="rm-row-sub">{DL[f.dev] || ''} · <span className="rm-red">{SC.rmRiskDetail(f)}</span></div><button className="rm-row-btn" onClick={() => mutate((fs) => SC.acknowledgeRisk(fs, f.id))}>Accept</button></div>
          )} />
          <Cat k="blockers" label="Blockers" list={blockers} row={(f) => (
            <div className="rm-row"><div className="rm-row-t" onClick={() => openCardModal(f.id)}>{f.title}</div><div className="rm-row-sub">{DL[f.dev] || ''} · <span className="rm-red"><IcFlag size={11} /> blocker</span></div><button className="rm-row-btn" onClick={() => mutate((fs) => SG.markBlocker(fs, f.id))}>Clear</button></div>
          )} />
          <Cat k="conflict" label="Conflicts" list={conflicts} row={(f) => (
            <div className="rm-row"><div className="rm-row-t" onClick={() => openCardModal(f.id)}>{f.title}</div><div className="rm-row-sub">{DL[f.dev] || ''} · overlaps a pinned card</div></div>
          )} />
          <Cat k="accepted" label="Accepted risks" list={accepted} row={(f) => (
            <div className="rm-row"><div className="rm-row-t" onClick={() => openCardModal(f.id)}>{f.title}</div><div className="rm-row-sub">{SC.rmRiskDetail(f)} · accepted</div><button className="rm-row-btn" onClick={() => mutate((fs) => SC.acknowledgeRisk(fs, f.id))}>Undo</button></div>
          )} />
          <div className="rm-side-hd rm-side-hd2">Unscheduled · drag onto a lane</div>
          {[['unbacklog', 'Now', unB], ['undesign', 'Design in Progress', unD], ['undc', 'Design Complete', unC]].map(([k, label, list]: any) => (
            <Cat key={k} k={k} label={label} list={list} row={(f) => (
              <div className="rm-urow" draggable onDragStart={() => { drag.current = { id: f.id, kind: 'unsched' } }} onClick={() => openCardModal(f.id)} title="Drag onto a developer’s lane">
                <div className="rm-urow-t">{f.title}
                  <button className="sched-mini" title="Schedule" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'schedule', id: f.id }) }}><IcCalendar size={11} /> Schedule</button>
                </div>
                {(f.status === 'design' || f.status === 'designcomplete') && <div className="rm-urow-sub">{f.designProgress || 0}% designed</div>}
                <div className="rm-urow-hint">⋮ drag onto a lane</div>
              </div>
            )} />
          ))}
        </div>
      </div>
      {rxOpen && (() => {
        const d = RX.custRoadmapData(cards, rxRange, rxReleased, true)
        const coming = RX.groupByMonth(d.coming, 'estCompletion')
        const released = RX.groupByMonth(d.released, 'completedOn').reverse()
        const download = () => {
          try {
            const blob = new Blob([RX.custRoadmapStandalone(cards, rxRange, rxReleased)], { type: 'text/html' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'wequote-product-roadmap.html'
            document.body.appendChild(a)
            a.click()
            a.remove()
            setTimeout(() => { try { URL.revokeObjectURL(url) } catch { /* noop */ } }, 1000)
            showToast('Roadmap downloaded as a standalone HTML file.')
          } catch { showToast('Download isn’t supported in this environment.') }
        }
        const printIt = () => {
          const w = window.open('', '_blank')
          if (!w) { showToast('Pop-ups are blocked — allow them to print.'); return }
          w.document.write(RX.custRoadmapStandalone(cards, rxRange, rxReleased))
          w.document.close()
          w.focus()
          setTimeout(() => w.print(), 300)
        }
        const Item: React.FC<{ f: Feature }> = ({ f }) => {
          const excl = f.showOnRoadmap === false
          return (
            <div className={'rx-item' + (excl ? ' rx-excluded' : '')}>
              <button className="rx-hidebtn" onClick={() => mutate((fs) => RX.rxToggleShow(fs, f.id))}>{excl ? 'Show' : 'Hide'}</button>
              <div className="rx-name">{custDisplayName(f)}</div>
              {custDescOf(f) && <div className="rx-desc">{custDescOf(f)}</div>}
            </div>
          )
        }
        const Section: React.FC<{ title: string; groups: { mo: string; items: Feature[] }[] }> = ({ title, groups }) =>
          groups.length === 0 ? null : (
            <div className="rx-section">
              <div className="rx-sec-h">{title}</div>
              {groups.map((g) => <div key={g.mo} className="rx-month"><div className="rx-month-h">{RX.custMonthLabel(g.mo + '-01')}</div>{g.items.map((f) => <Item key={f.id} f={f} />)}</div>)}
            </div>
          )
        return (
          <div className="overlay open" style={{ zIndex: 130 }} onClick={(e) => { if ((e.target as HTMLElement).classList.contains('overlay')) setRxOpen(false) }}>
            <div className="modal" style={{ width: 760, maxHeight: '92vh' }}>
              <div className="rx-toolrow">
                <h2 style={{ marginBottom: 0 }}>Customer roadmap — preview</h2>
                <div className="rx-tools">
                  <label>Show <select value={rxRange} onChange={(e) => setRxRange(e.target.value as RX.RxRange)}>
                    <option value="all">All upcoming</option>
                    <option value="month">This month</option>
                    <option value="next3">Next 3 months</option>
                    <option value="year">This year</option>
                    <option value="nextyear">Next year</option>
                  </select></label>
                  <label className="rq-design"><input type="checkbox" checked={rxReleased} onChange={(e) => setRxReleased(e.target.checked)} /> Include recently released</label>
                </div>
              </div>
              <div className="rx-actionsrow">
                <button className="btn-s" onClick={printIt}><IcPrinter size={13} /> Print / Save as PDF</button>
                <button className="btn-s" onClick={download}><IcDownload size={13} /> Download HTML</button>
                <button className="btn-s" onClick={() => setRxOpen(false)}>Close</button>
              </div>
              <div className="rx-previewwrap">
                <div className="rx-doc">
                  <div className="rx-head"><div className="rx-brand">WeQuote</div><div className="rx-h1">Product Roadmap</div><div className="rx-sub">What we’re building and when · updated {fmtDate(new Date().toISOString().slice(0, 10))}</div></div>
                  {coming.length === 0 && released.length === 0
                    ? <div className="rx-empty">Nothing to show for this range. Try widening the date range or ticking “Include recently released”.</div>
                    : <><Section title="Coming up" groups={coming} /><Section title="Recently released" groups={released} /></>}
                  <div className="rx-foot">Timings are indicative and may change.</div>
                </div>
              </div>
            </div>
          </div>
        )
      })()}
      {confirmBO && (
        <div className="overlay open" style={{ zIndex: 130 }} onClick={(e) => { if ((e.target as HTMLElement).classList.contains('overlay')) setConfirmBO(null) }}>
          <div className="modal" style={{ width: 420 }}>
            <h2>Remove block-out</h2>
            <div className="modal-sub">Remove block-out “{confirmBO.name}”? This can’t be undone.</div>
            <div className="factions"><button className="btn-s" onClick={() => setConfirmBO(null)}>Cancel</button><button className="btn-p" onClick={() => { void removeBlockoutAction(confirmBO.id).then(() => setConfirmBO(null)) }}>Remove</button></div>
          </div>
        </div>
      )}
    </div>
  )
}
